import { Areas, FlowStop as FlowStopType, SingularStationArea, Templates } from '@thive/fddm-types';
import { Stack } from '@mui/system';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { logger } from '@common/logging/logger';
import { areasToFlatCollection, isRackingArea } from '@/modules/floorplanDesign/helpers';
import { Area, Template } from '@/modules/floorplanDesign/types';
import { isAtLeast1 } from '@/modules/floorplanDesign/helpers/minItems';
import { generateNewGroupName } from '@/modules/floorplanDesign/helpers/name';
import { getLoadHandlingTypeLabel } from '@/modules/floorplanDesign/helpers/singularStationAreaHelpers';
import { isShuttleStorageRackingArea } from '@/modules/floorplanDesign/helpers/typeguards';
import { FddmServiceFactory as FloorplanDesignServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { getShuttleStorageAreaByBaseId } from '@/modules/flowsV2/flowEndpoint';

import { NoFlowStops } from './NoFlowStops';
import { FlowStop } from './FlowStop';


type AreaItem = { value: string; label: string };
type TemplateItem = { value: string; label: string, areaIds: string[] };

const getAreaLoadHandlingType = (area: Area): SingularStationArea['loadHandlingType'] | null => {
  if (isRackingArea(area)) return area.rackingLoadHandlingType;
  if ('loadHandlingType' in area) return area.loadHandlingType;

  return null;
};

const toAreaItem = (area: Area): AreaItem => ({ value: area.id, label: area.name });
const toTemplateItem = (template: Template): TemplateItem => ({ value: template.group.id, label: template.group.name, areaIds: template.group.areaIds });

interface Props {
  flowStops: FlowStopType[];
  selectedIds: string[];
  areas: Areas;
  templates: Templates;
  showDraft: boolean;
  disabled?: boolean;
  onDismissDraft: () => void;
}

function FlowStopsComponent({
  flowStops,
  selectedIds,
  areas,
  templates,
  showDraft,
  disabled = false,
  onDismissDraft,
}: Props) {
  const fddmSvc = useMemo(
    () => FloorplanDesignServiceFactory.getInstance().createFddmService(),
    [],
  );

  const [draftFlowStopId, setDraftFlowStopId] = useState<string | null>(null);

  useEffect(() => {
    // plz fix me
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!showDraft) setDraftFlowStopId(null);
  }, [showDraft]);

  const allAreas = useMemo(() => areasToFlatCollection(areas), [areas]);
  const allAreaItems = useMemo(() => allAreas.map(toAreaItem), [allAreas]);
  const allShuttleTemplates = useMemo(() => templates.shuttleRacks, [templates]);
  const allTemplateItems = useMemo(
    () => allShuttleTemplates.map(toTemplateItem),
    [allShuttleTemplates],
  );

  const usedAreaIds = useMemo(
    () => new Set(flowStops.flatMap((flowStop) => flowStop.areaIds)),
    [flowStops],
  );

  const usedTemplateIds = useMemo(
    () => new Set(allTemplateItems.filter((template) => template.areaIds.some((areaId) => usedAreaIds.has(areaId))).map((template) => template.value)),
    [allTemplateItems, usedAreaIds],
  );

  const draftFlowStop = useMemo(
    () => flowStops.find((flowStop) => flowStop.id === draftFlowStopId) ?? null,
    [draftFlowStopId, flowStops],
  );

  const visibleFlowStops = useMemo(() => {
    const selectedSet = new Set(selectedIds);

    // template id is in selectedIds, but flow stop areaIds are not template ids, so we need to get the area ids for the selected templates
    const selectedTemplateAreaIds = allTemplateItems
      .filter((template) => selectedSet.has(template.value))
      .flatMap((template) => template.areaIds);
    selectedTemplateAreaIds.forEach((id) => selectedSet.add(id));

    return flowStops.filter((flowStop) => {
      if (flowStop.id === draftFlowStopId) return false;
      if (selectedSet.size === 0) return true;

      return (
        selectedSet.has(flowStop.id) || flowStop.areaIds.some((areaId) => selectedSet.has(areaId))
      );
    });
  }, [allTemplateItems, draftFlowStopId, flowStops, selectedIds]);

  const handleSelectArea = useCallback(
    (flowStop: FlowStopType, itemId: string) => {
      if (disabled) return;

      // if a shuttle template is selected, add the shuttle storage area to the flow stop
      const shuttleTemplate = allShuttleTemplates.find((st) => st.group.id === itemId);
      if (shuttleTemplate) {
        const shuttleStorageArea = getShuttleStorageAreaByBaseId(shuttleTemplate.group.id);
        if (!shuttleStorageArea) return;

        fddmSvc.updateFlowStop({ ...flowStop, areaIds: [...flowStop.areaIds, shuttleStorageArea.id] });
        return;
      }

      fddmSvc.updateFlowStop({ ...flowStop, areaIds: [...flowStop.areaIds, itemId] });
    },
    [allShuttleTemplates, disabled, fddmSvc],
  );

  const handleRenameFlowStop = useCallback(
    (flowStop: FlowStopType, name: string) => {
      if (disabled) return;
      if (name === flowStop.name) return;

      fddmSvc.updateFlowStop({ ...flowStop, name });
    },
    [disabled, fddmSvc],
  );

  const handleRemoveFlowStop = useCallback(
    (flowStop: FlowStopType) => {
      if (disabled) return;

      fddmSvc.deleteFlowStops([flowStop]);

      if (flowStop.id === draftFlowStopId) {
        onDismissDraft();
      }
    },
    [disabled, draftFlowStopId, fddmSvc, onDismissDraft],
  );

  const handleRemoveArea = useCallback(
    (flowStop: FlowStopType, areaId: string) => {
      if (disabled) return;

      const updatedAreaIds = flowStop.areaIds.filter((id) => id !== areaId);

      if (!isAtLeast1(updatedAreaIds)) {
        handleRemoveFlowStop(flowStop);
        return;
      }

      fddmSvc.updateFlowStop({ ...flowStop, areaIds: updatedAreaIds });
    },
    [disabled, fddmSvc, handleRemoveFlowStop],
  );

  const getSelectableAreasFor = useCallback(
    (flowStop: FlowStopType): AreaItem[] => {
      const referenceArea = allAreas.find((area) => area.id === flowStop.areaIds[0]);
      const referenceLoadHandlingType = referenceArea
        ? getAreaLoadHandlingType(referenceArea)
        : null;

      if (!referenceArea || !referenceLoadHandlingType) {
        logger.error('No reference area / load handling type for flow stop', flowStop.id);
        return [];
      }

      return allAreas
        .filter(
          (area) =>
            !usedAreaIds.has(area.id) &&
            area.areaType === referenceArea.areaType &&
            getAreaLoadHandlingType(area) === referenceLoadHandlingType &&
            (!allShuttleTemplates.some((template) => template.group.areaIds.includes(area.id)) ||
              isShuttleStorageRackingArea(area)) &&
            ![...usedTemplateIds].some((templateId) =>
              allShuttleTemplates.find(
                (t) => t.group.id === templateId && t.group.areaIds.includes(area.id),
              ),
            ),
        )
        .map(toAreaItem);
    },
    [allAreas, allShuttleTemplates, usedAreaIds, usedTemplateIds],
  );

  const getAreaTypeLabel = useCallback(
    (flowStop: FlowStopType): string | undefined => {
      const referenceArea = allAreas.find((area) => area.id === flowStop.areaIds[0]);
      const loadHandlingType = referenceArea ? getAreaLoadHandlingType(referenceArea) : null;

      return loadHandlingType ? getLoadHandlingTypeLabel(loadHandlingType) : undefined;
    },
    [allAreas],
  );

  const draftSelectableAreas = useMemo<
    AreaItem[] | TemplateItem[] | (AreaItem[] | TemplateItem[])
  >(() => {
    if (draftFlowStop) return getSelectableAreasFor(draftFlowStop);

    return allAreas
      .filter(
        (area) =>
          !usedAreaIds.has(area.id) &&
          getAreaLoadHandlingType(area) !== null &&
          (!allShuttleTemplates.some((template) =>
            template.group.areaIds.includes(area.id),
          ) ||
            isShuttleStorageRackingArea(area)) &&
          ![...usedTemplateIds].some((templateId) =>
            allShuttleTemplates.find(
              (t) => t.group.id === templateId && t.group.areaIds.includes(area.id),
            ),
          ),
      )
      .map(toAreaItem);
  }, [allAreas, allShuttleTemplates, draftFlowStop, getSelectableAreasFor, usedAreaIds, usedTemplateIds]);

  const handleCreateFromDraft = useCallback(
    (itemId: string) => {
      if (disabled) return;

      const id = uuid();
      const name = generateNewGroupName('Flow Stop');

      // if a shuttle template was selected, create flow stop with its shuttle storage area
      const shuttleTemplate = allShuttleTemplates.find((st) => st.group.id === itemId);
      if (shuttleTemplate) {
        const shuttleStorageArea = getShuttleStorageAreaByBaseId(shuttleTemplate.group.id);
        if (!shuttleStorageArea) return;

        const newFlowStop: FlowStopType = {
          id,
          name,
          areaIds: [shuttleStorageArea.id],
        };

        setDraftFlowStopId(id);
        fddmSvc.addFlowStops([newFlowStop]);
        return;
      }

      const newFlowStop: FlowStopType = {
        id,
        name,
        areaIds: [itemId],
      };

      setDraftFlowStopId(id);
      fddmSvc.addFlowStops([newFlowStop]);
    },
    [allShuttleTemplates, disabled, fddmSvc],
  );

  const handleOverwriteFlowStop = useCallback(
    (flowStop: FlowStopType, areaId: string, previousAreaId: string) => {
      if (disabled) return;

      const updatedAreaIds = flowStop.areaIds.map((id) => (id === previousAreaId ? areaId : id));

      if (!isAtLeast1(updatedAreaIds)) {
        handleRemoveFlowStop(flowStop);
        return;
      }

      fddmSvc.updateFlowStop({ ...flowStop, areaIds: updatedAreaIds });
    },
    [disabled, fddmSvc, handleRemoveFlowStop],
  );

  return (
    <Stack gap={2}>
      {!showDraft && (
        <NoFlowStops
          allFlowStopsAmount={flowStops.length}
          relatedFlowStopsAmount={visibleFlowStops.length}
        />
      )}

      {visibleFlowStops.map((flowStop) => (
        <FlowStop
          key={flowStop.id}
          flowStop={flowStop}
          allAreaItems={allAreaItems}
          selectableAreaItems={getSelectableAreasFor(flowStop)}
          areaType={getAreaTypeLabel(flowStop)}
          disabled={disabled}
          onOverwriteArea={handleOverwriteFlowStop}
          onSelectArea={handleSelectArea}
          onRemoveArea={handleRemoveArea}
          onRemoveFlowStop={handleRemoveFlowStop}
          onRename={handleRenameFlowStop}
        />
      ))}

      {showDraft && (
        <FlowStop
          key='draft'
          flowStop={draftFlowStop}
          allAreaItems={allAreaItems}
          selectableAreaItems={draftSelectableAreas}
          areaType={draftFlowStop ? getAreaTypeLabel(draftFlowStop) : undefined}
          disabled={disabled}
          onSelectArea={handleSelectArea}
          onRemoveArea={handleRemoveArea}
          onRemoveFlowStop={handleRemoveFlowStop}
          onRename={handleRenameFlowStop}
          onCreate={handleCreateFromDraft}
          onDismissDraft={onDismissDraft}
        />
      )}
    </Stack>
  );
}

export const FlowStops = memo(FlowStopsComponent);
